import { homepageArticles } from './home';

export interface ArticleDetail {
  id: string;
  title: string;
  excerpt: string;
  category: string;
  categorySlug: string;
  date: string;
  readTime: string;
  imageUrl: string;
  content: string;
  relatedIds: string[];
}

export const articleDetails: Record<string, ArticleDetail> = {
  'article-1': {
    id: 'article-1',
    title: '月下独酌：古人如何在月光中寻得诗意',
    excerpt: '月色如水，洒落庭院。千百年来，无数文人墨客在月下独酌，将对人生的感悟化作千古绝唱。',
    category: '月下风流',
    categorySlug: 'yuexia-fengliu',
    date: '2026-08-05',
    readTime: '8分钟',
    imageUrl: homepageArticles[0].imageUrl,
    content: `<p>月色如水，洒落庭院。千百年来，无数文人墨客在月下独酌，将对人生的感悟化作千古绝唱。从李白的"举杯邀明月，对影成三人"到苏轼的"明月几时有，把酒问青天"，月光与酒，已经成为中国文化中最动人的意象组合。</p>

<h2>一、月光里的孤独与自由</h2>

<p>月下独酌，表面看是孤独，实际上却是一种难得的自由。李白在《月下独酌》中写道："花间一壶酒，独酌无相亲。举杯邀明月，对影成三人。"诗人将明月和自己的影子都视为酒友，在孤独中创造出热闹的场景。这种将孤独转化为诗意享受的能力，正是中国古代文人最令人羡慕的精神境界。</p>

<p>在月光之下，没有世俗的纷扰，没有功名的牵绊，只有纯粹的自我与天地对话。这种"独与天地精神往来"的境界，是风月文化最核心的精神内涵。</p>

<h2>二、月色中的思乡与怀人</h2>

<p>月光还是连接远方亲人的纽带。张九龄在《望月怀远》中写道："海上生明月，天涯共此时。"无论相隔多远，我们仰望的是同一轮明月。这种共时性的体验，让月光成为中国文化中思念的最佳载体。</p>

<p>从古至今，无数游子在月夜想起故乡、想起亲人。月光的温柔与遥远，恰好呼应了思念的情感质地——柔软却绵长，遥远却真切。</p>

<h2>三、当代人的月下时光</h2>

<p>在喧嚣的现代生活中，月下独酌的传统似乎已经远去。但我们依然可以在某个安静的夜晚，关掉手机，倒一杯清茶或小酒，走到阳台或庭院，静静地看着月亮。那一刻，古人的诗意感受依然可以抵达我们的内心。</p>

<p>风月文化不是一种怀旧的姿态，而是一种让心灵获得片刻宁静的生活方式。在月光下，我们能够暂时放下白天的忙碌与焦虑，与自己的内心对话，感受那些被日常淹没的细腻情感。</p>

<p>月下独酌，不一定要喝酒，也不一定要真的"独酌"。泡一杯茶，听一首古曲，或者只是静静地坐着，都是对这份传统的致敬与延续。</p>

<blockquote>
  <p>"人生代代无穷已，江月年年望相似。"——张若虚《春江花月夜》</p>
</blockquote>

<p>月亮还是那个月亮，变化的只是我们自己。在这个快节奏的时代，愿我们都能偶尔停下来，在月光中找到那份属于自己的诗意与宁静。</p>`,
    relatedIds: ['article-2', 'article-3', 'article-6'],
  },
  'article-2': {
    id: 'article-2',
    title: '茶香袅袅：一壶清茶里的风雅人生',
    excerpt: '茶，是风雅生活的最佳伴侣。从选茶、煮水到品饮，每一个环节都蕴含着东方美学的精髓。',
    category: '情趣雅致',
    categorySlug: 'qingqu-yazhi',
    date: '2026-08-02',
    readTime: '6分钟',
    imageUrl: homepageArticles[1].imageUrl,
    content: `<p>茶，是风雅生活的最佳伴侣。从选茶、煮水到品饮，每一个环节都蕴含着东方美学的精髓。在茶香氤氲中，我们找到了与自我对话的最佳方式。</p>

<h2>一、茶之起源：一片叶子的千年传奇</h2>

<p>中国是茶的故乡。相传神农尝百草，日遇七十二毒，得茶而解之。从药用、食用到饮用，茶走过了数千年的历程。唐代陆羽《茶经》的问世，标志着中国茶文化的正式确立。自此，"茶道"成为中国人精神生活的重要组成部分。</p>

<p>"茶之为饮，发乎神农氏，闻于鲁周公。"茶从一开始就与文人雅士结下了不解之缘。一杯清茶，一卷诗书，便是文人最理想的生活状态。</p>

<h2>二、茶席之美：方寸之间的天地</h2>

<p>一个精致的茶席，是风雅生活的缩影。茶壶、茶杯、茶则、茶针……每一件器物都承载着匠人的心血与审美追求。紫砂的古朴、白瓷的纯净、青瓷的温润，不同的茶具材质与不同品类的茶叶相配，构成了丰富而精妙的品茶体验。</p>

<p>布置茶席本身，就是一种美的修行。席面的铺设、器物的摆放、花艺的点缀，每一个细节都需要用心斟酌。在这个过程中，人的心境也随之沉静下来。</p>

<h2>三、品茶之道：观色、闻香、品味</h2>

<p>品茶是一门综合性的感官艺术。观其色——清澈明亮为上；闻其香——清雅持久为妙；品其味——醇厚回甘为佳。三者的结合，构成了一次完整的品茶体验。</p>

<p>更重要的，是品茶时的心境。心浮气躁时，再好的茶也品不出真味。唯有放下杂念，专注当下，才能真正体会到茶之真趣。正所谓"茶禅一味"，品茶即是修行。</p>

<p>在这个快节奏的时代，不妨为自己留出一段安静的茶时光。泡一壶好茶，看茶叶在水中舒展，闻茶香在空气中弥漫，让心灵在茶香中获得片刻的安宁。</p>`,
    relatedIds: ['article-1', 'article-4', 'article-5'],
  },
  'article-3': {
    id: 'article-3',
    title: '花间一壶酒：诗词中的风月意象探微',
    excerpt: '风与月，是中国古典诗词中最常见的意象组合。它们不仅代表着自然之美，更承载着文人对自由、爱情与理想的无限向往。',
    category: '文化神韵',
    categorySlug: 'wenhua-shenyun',
    date: '2026-07-28',
    readTime: '10分钟',
    imageUrl: homepageArticles[2].imageUrl,
    content: `<p>风与月，是中国古典诗词中最常见的意象组合。"风月"二字，承载了太多的文化内涵——自由、浪漫、清雅、闲适。从先秦到唐宋，从诗词到书画，风月意象贯穿了整部中国文学史。</p>

<h2>一、风月意象的起源与演变</h2>

<p>"风"与"月"作为独立意象，在《诗经》中就已大量出现。"关关雎鸠，在河之洲"的背后是春风；"月出皎兮，佼人僚兮"直接描写了月下的美人。到了魏晋南北朝，"风月"开始作为一个固定词组出现，成为文人雅士追捧的审美对象。</p>

<p>《世说新语》中记载："清风明月，辄思玄度。"清风明月已经成为触发文人情思的标配场景。许询（字玄度）是东晋名士，以清谈著称。每遇清风明月，人们便想起他的风采——风月，就这样成为了高雅人格的象征。</p>

<h2>二、唐诗中的风月境界</h2>

<p>唐代是风月诗词的黄金时代。李白的"清风明月不用一钱买"，道出了风月最珍贵的品质——它们是自然赐予每个人的免费礼物，不分贵贱，人人可得。这种平等的审美体验，让风月成为最民主的美学享受。</p>

<p>杜甫的"星垂平野阔，月涌大江流"，将风月意象提升到了宏大的宇宙视角。而王维的"明月松间照，清泉石上流"，则将风月之美融入山水之间，创造了一种空灵澄澈的意境。</p>

<h2>三、宋词中的风月情韵</h2>

<p>到了宋代，风月在词中获得了更为细腻的表达。柳永的"杨柳岸，晓风残月"，以风月烘托离别的伤感；苏轼的"但愿人长久，千里共婵娟"，将月光的祝福送向远方；辛弃疾的"明月别枝惊鹊，清风半夜鸣蝉"，捕捉了风月中最灵动的瞬间。</p>

<p>风月不再只是自然景观，而是承载了丰富情感的文化符号。爱、别离、思念、孤独、豁达——人类的种种情感，都可以在风月中找到对应的表达。</p>

<h2>四、风月之美的当代传承</h2>

<p>今天，当我们说起"风月"时，可能更习惯性地想到"风花雪月"——一个略带贬义的词，暗示不切实际的浪漫幻想。但回归风月的本意，它是一种对美好生活的执着追求，是一种在平凡中创造诗意的能力。</p>

<p>爱风月，不是逃避现实，而是给现实生活注入美的灵魂。让风月之美，在我们的日常生活中继续绽放。</p>`,
    relatedIds: ['article-1', 'article-6', 'article-5'],
  },
  'article-4': {
    id: 'article-4',
    title: '庭院深深：打造属于你的风雅空间',
    excerpt: '一方庭院，便是整个天地。无论居所大小，都可以通过巧妙布置，营造出充满风雅情趣的生活空间。',
    category: '风雅生活',
    categorySlug: 'fengya-shenghuo',
    date: '2026-07-25',
    readTime: '7分钟',
    imageUrl: homepageArticles[3].imageUrl,
    content: `<p>一方庭院，便是整个天地。中国文人自古以来就有一个庭院梦——在喧嚣的尘世之外，拥有一片属于自己的小天地。在这个空间里，可以读书、品茶、赏花、望月，过上自由自在的风雅生活。</p>

<h2>一、庭院的文化渊源</h2>

<p>中国园林艺术源远流长。从汉代的上林苑到明清的苏州园林，庭院一直是中国人安放身心的理想空间。"结庐在人境，而无车马喧"——陶渊明用朴素的语言描述了庭院生活的最高境界。</p>

<p>庭院的精髓在于"借景"与"造境"。通过巧妙的布局，将有限的空间转化为无限的意境。一块石头可以是一座山，一池水可以是一片湖——这就是中国人的造园智慧。</p>

<h2>二、现代居所中的风雅营造</h2>

<p>如今，拥有独立庭院已经是一种奢侈。但风雅空间的营造，并不一定要依赖实体庭院。一个阳台、一个窗台、甚至室内的一角，都可以成为你的"风雅天地"。</p>

<p>关键要素有三：绿植、光影和意境。一盆文竹、一株兰花，就能为空间注入生机；一盏暖灯、一扇竹帘，就能营造柔和的光影；一幅字画、一件瓷器，就能提升空间的格调。</p>

<h2>三、打造属于你的风雅角落</h2>

<p>选择家中最安静的角落——可以是靠窗的书桌，可以是阳台的一隅，也可以是客厅的一角。铺上一张素雅的桌布，摆上一套茶具，放上一本喜爱的书，再点缀一点绿植。不需要复杂的布置，简洁、雅致、舒适便是最好的状态。</p>

<p>最重要的是，这个空间要让你感到放松和愉悦。在这里，你可以暂时忘记外界的纷扰，专注于自己喜欢的事情——喝茶、读书、写字、发呆，或者什么都不做，只是静静地感受时光的流淌。</p>

<p>风雅生活，不在远方，就在你的身边。只需要一点用心，就能在日常生活中创造出属于自己的诗意空间。</p>`,
    relatedIds: ['article-2', 'article-5', 'article-6'],
  },
  'article-5': {
    id: 'article-5',
    title: '琴瑟和鸣：古乐中的浪漫与深情',
    excerpt: '古琴一曲，胜过千言万语。在中国传统音乐中，琴瑟之音不仅代表着最高雅的审美追求，更是表达深情厚意的最佳载体。',
    category: '古风美学',
    categorySlug: 'gufeng-meixue',
    date: '2026-07-20',
    readTime: '9分钟',
    imageUrl: homepageArticles[4].imageUrl,
    content: `<p>古琴一曲，胜过千言万语。在中国传统音乐中，琴瑟之音不仅代表着最高雅的审美追求，更是表达深情厚意的最佳载体。聆听古乐，感受穿越千年的浪漫。</p>

<h2>一、琴瑟和鸣：中国最古老的爱情隐喻</h2>

<p>"妻子好合，如鼓瑟琴"——《诗经》中的这句话，将琴瑟之和比作夫妻之间的和谐。从此，"琴瑟和鸣"成为了中国人表达美好婚姻和爱情的标准用语。</p>

<p>琴与瑟本是两种不同的乐器，但它们的合奏却能产生最美妙的和声。这种和谐不是千篇一律的相同，而是在差异中寻求共鸣——这正是中国文化对爱情最深刻的理解。</p>

<h2>二、高山流水：知音之情的千古绝唱</h2>

<p>伯牙善鼓琴，钟子期善听。伯牙鼓琴志在高山，钟子期曰："善哉，峨峨兮若泰山。"志在流水，钟子期曰："善哉，洋洋兮若江河。"钟子期死，伯牙破琴绝弦，终身不复鼓琴。</p>

<p>"高山流水"的故事之所以流传千古，在于它道出了人与人之间最深层的渴望——被理解。在这个意义上，"知音"超越了爱情和友情，成为一种极为珍贵的人生际遇。</p>

<h2>三、当代生活中的古乐之美</h2>

<p>在今天的日常生活中，古乐依然有其独特的魅力。工作的间隙，播放一曲《平沙落雁》或《渔樵问答》，让悠扬的琴声洗去心头的浮躁；夜晚独处时，听一曲《梅花三弄》，在琴音中感受古人的心境。</p>

<p>古乐之美不在于技巧的高超，而在于它所传达的情感和意境。每一首古曲背后，都有一个动人的故事，都承载着创作者对人生的感悟与思考。</p>

<p>让古乐走进自己的生活，不是为了附庸风雅，而是为了在纷繁的现代生活中，找到一方能够安放灵魂的净土。</p>`,
    relatedIds: ['article-6', 'article-1', 'article-3'],
  },
  'article-6': {
    id: 'article-6',
    title: '二十四节气中的风月之美',
    excerpt: '春有百花秋有月，夏有凉风冬有雪。二十四节气不仅是农耕文明的智慧结晶，更蕴含着中国人对自然之美的细腻感知。',
    category: '诗意浪漫',
    categorySlug: 'shiyi-langman',
    date: '2026-07-15',
    readTime: '12分钟',
    imageUrl: homepageArticles[5].imageUrl,
    content: `<p>春有百花秋有月，夏有凉风冬有雪。二十四节气不仅是农耕文明的智慧结晶，更蕴含着中国人对自然之美的细腻感知。跟随节气流转，感受时光中的风月之美。</p>

<h2>一、节气：中国人的时间美学</h2>

<p>二十四节气是中国古代的一项伟大创造。它将一年分为二十四个时间段，每个节气都有独特的物候特征和文化内涵。在农耕时代，节气指导着人们的生产生活；在今天，节气则成为我们感知自然节律、体味生活之美的重要参照。</p>

<p>"春雨惊春清谷天，夏满芒夏暑相连。秋处露秋寒霜降，冬雪雪冬小大寒。"这首节气歌，是中国人的文化记忆，也是风月之美的天然节拍器。</p>

<h2>二、四时风月：每个季节都有独特的诗意</h2>

<p><strong>春风：</strong>"春风又绿江南岸"——春天的风带着生机与希望。立春、雨水、惊蛰、春分、清明、谷雨，每一个春季节气都在诉说着万物复苏的故事。春风拂面，花开有声，这是最温柔的风月时光。</p>

<p><strong>夏月：</strong>"荷风送香气，竹露滴清响"——夏天的月最为明亮。立夏、小满、芒种、夏至、小暑、大暑，夏夜赏月是古人最爱的消暑方式。在月光下纳凉、听蝉、品茗，这是最惬意的风月时光。</p>

<p><strong>秋风：</strong>"秋风吹不尽，总是玉关情"——秋风带着一丝凉意与思念。立秋、处暑、白露、秋分、寒露、霜降，秋风中的落叶与明月，构成了一幅醉人的画卷。</p>

<p><strong>冬月：</strong>"晚来天欲雪，能饮一杯无"——冬月最为清冷也最为纯净。立冬、小雪、大雪、冬至、小寒、大寒，冬夜的月光照在雪地上，银装素裹的世界宛如仙境。</p>

<h2>三、在日常生活中践行节气美学</h2>

<p>跟着节气生活，是一种优雅的生活方式。春分竖蛋、清明踏青、立夏尝新、冬至吃饺子——这些看似简单的习俗背后，是中国人对自然节奏的尊重与顺应。</p>

<p>在每一个节气更替的时刻，不妨停下来感受一下身边的变化：风的温度、花的盛开、叶的颜色、月的圆缺。让二十四节气成为你感知风月之美的天然日历。</p>`,
    relatedIds: ['article-4', 'article-1', 'article-3'],
  },
};

export function getArticlesByCategory(categorySlug: string) {
  return homepageArticles.filter((a) => a.categorySlug === categorySlug);
}

export function getCategoryName(slug: string): string {
  const map: Record<string, string> = {
    'fengya-shenghuo': '风雅生活',
    'yuexia-fengliu': '月下风流',
    'qingqu-yazhi': '情趣雅致',
    'gufeng-meixue': '古风美学',
    'shiyi-langman': '诗意浪漫',
    'wenhua-shenyun': '文化神韵',
  };
  return map[slug] || slug;
}